import { useEffect, useMemo, useRef, useState } from "react";

import {
  useDeleteCatalogBlock,
  useDeleteCatalogPosition,
  useInvoiceBlocks,
  useInvoiceCatalog,
  useSaveCatalogPosition,
} from "../../hooks/useInvoices";
import { usePflanzenAngebot, type PflanzeTreffer } from "../../hooks/usePflanzen";
import { euro, type CatalogBlock, type CatalogPosition } from "../../lib/invoices";

/**
 * Das Titelfeld einer Position — mit Bibliothekssuche direkt darin.
 *
 * Vorher stand über der Liste ein eigenes Feld „Aus Positionskatalog
 * hinzufügen". Das war ein zweiter Ort für dieselbe Sache: wer eine Position
 * erfasst, tippt ohnehin ihren Titel. Jetzt schlägt das Tippen im Titel
 * passende Einträge vor — **wählt man keinen, bleibt es freier Text**. Die
 * Suche drängt sich also nicht auf, sie hilft nur.
 *
 * **Zwei Betriebsarten, bewusst getrennt** (Benjamin, 16.09.2026): im
 * Positionstitel werden nur Positionen vorgeschlagen, in der Blocküberschrift
 * nur Bausteine. Vorher lagen beide in derselben Liste — wer eine Position
 * erfasste, bekam Bausteine angeboten, die seine Zeile durch sieben andere
 * ersetzt hätten, und wer einen Block benannte, bekam gar nichts. Der Ort, an
 * dem getippt wird, sagt schon, was gesucht ist.
 */

export type TitelFeldProps = {
  /** Was vorgeschlagen wird — der Ort des Feldes entscheidet, nicht der Nutzer. */
  art?: "position" | "block";
  wert: string;
  onWert: (wert: string) => void;
  /** Eine Katalogposition wurde gewählt — Preis, Einheit und Text übernehmen. */
  onPosition?: (item: CatalogPosition) => void;
  /** Ein Baustein wurde gewählt — was damit geschieht, entscheidet der Aufrufer. */
  onBlock?: (block: CatalogBlock) => void;
  /** Der Blockkopf öffnet sein Formular von selbst und will den Fokus haben. */
  autoFocus?: boolean;
};

/** Ab hier lohnt die Suche; darunter träfe sie fast den ganzen Katalog. */
const AB_ZEICHEN = 2;

export default function InvoiceTitelFeld({
  art = "position", wert, onWert, onPosition, onBlock, autoFocus,
}: TitelFeldProps) {
  const [offen, setOffen] = useState(false);
  const [markiert, setMarkiert] = useState(-1);
  const box = useRef<HTMLDivElement>(null);

  const suche = wert.trim();
  const aktiv = offen && suche.length >= AB_ZEICHEN;
  // `enabled` erst ab der Mindestlänge: sonst lädt jede leere Zeile beim
  // Fokussieren den halben Katalog.
  const positionen = useInvoiceCatalog(suche, aktiv && art === "position").data?.items || [];
  const bausteine = useInvoiceBlocks(suche, aktiv && art === "block").data?.items || [];
  // Pflanzenbibliothek (16.10.2026): im Positionstitel stehen Pflanzen mit Qualität und Preis
  // unter den Katalogpositionen — erst ab drei Zeichen, sonst trifft „Ro“ jede Rose.
  const pflanzen = usePflanzenAngebot(suche, aktiv && art === "position").data?.items || [];

  type Treffer =
    | { art: "block"; block: CatalogBlock }
    | { art: "position"; position: CatalogPosition }
    | { art: "pflanze"; pflanze: PflanzeTreffer };
  const treffer = useMemo<Treffer[]>(() => (art === "block"
    ? bausteine.slice(0, 8).map((b) => ({ art: "block" as const, block: b }))
    : [
      ...positionen.slice(0, pflanzen.length ? 5 : 8).map((p) => ({ art: "position" as const, position: p })),
      ...pflanzen.slice(0, 6).map((p) => ({ art: "pflanze" as const, pflanze: p })),
    ]
  ), [art, bausteine, positionen, pflanzen]);

  useEffect(() => setMarkiert(-1), [suche]);

  /**
   * Bibliothek pflegen, ohne den Faden zu verlieren.
   *
   * Wählt jemand bewusst einen Treffer aus, kann er ihn hier bearbeiten oder
   * löschen — statt in die Einstellungen zu wechseln und den angefangenen
   * Beleg zu verlassen (Benjamin, 01.09.2026). Der Beleg selbst bleibt dabei
   * unangetastet: eine schon übernommene Zeile ist eine eigene Kopie, keine
   * Referenz auf den Katalogeintrag.
   */
  const [bearbeitetSchluessel, setBearbeitetSchluessel] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState<CatalogPosition | null>(null);
  const [loeschenSchluessel, setLoeschenSchluessel] = useState<string | null>(null);
  const [pflegeHinweis, setPflegeHinweis] = useState("");
  const speichern = useSaveCatalogPosition();
  const positionLoeschen = useDeleteCatalogPosition();
  const blockLoeschen = useDeleteCatalogBlock();

  const bearbeitenOeffnen = (position: CatalogPosition) => {
    setLoeschenSchluessel(null);
    setBearbeitetSchluessel(position.title);
    setEntwurf({ ...position });
    setPflegeHinweis("");
    // Der Dialog steht für sich (siehe unten) — die Trefferliste dahinter
    // schließen, statt sie offen und ungenutzt stehen zu lassen.
    setOffen(false);
  };

  const bearbeitenSpeichern = async () => {
    if (!entwurf || !bearbeitetSchluessel) return;
    const neuerTitel = entwurf.title.trim();
    if (!neuerTitel) { setPflegeHinweis("Ohne Titel lässt sich nichts speichern."); return; }
    try {
      await speichern.mutateAsync({ ...entwurf, title: neuerTitel });
      // Der Titel ist der Schlüssel — wurde er geändert, bliebe sonst der
      // alte Eintrag als Dublette stehen.
      if (neuerTitel.toLowerCase() !== bearbeitetSchluessel.toLowerCase()) {
        await positionLoeschen.mutateAsync(bearbeitetSchluessel);
      }
      setBearbeitetSchluessel(null);
      setEntwurf(null);
    } catch {
      setPflegeHinweis("Speichern fehlgeschlagen — bitte nochmal versuchen.");
    }
  };

  const loeschenBestaetigen = async (art: "position" | "block", titel: string) => {
    try {
      if (art === "position") await positionLoeschen.mutateAsync(titel);
      else await blockLoeschen.mutateAsync(titel);
      setLoeschenSchluessel(null);
    } catch {
      setPflegeHinweis("Löschen fehlgeschlagen — bitte nochmal versuchen.");
    }
  };

  useEffect(() => {
    if (!aktiv) return;
    const schliessen = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOffen(false);
    };
    document.addEventListener("mousedown", schliessen);
    return () => document.removeEventListener("mousedown", schliessen);
  }, [aktiv]);

  const uebernehmen = (index: number) => {
    const t = treffer[index];
    if (!t) return;
    if (t.art === "block") onBlock?.(t.block);
    else if (t.art === "pflanze") onPosition?.(t.pflanze);
    else onPosition?.(t.position);
    setOffen(false);
    setMarkiert(-1);
  };

  /**
   * Pfeiltasten wandern durch die Vorschläge, Enter übernimmt den markierten.
   * **Enter ohne Markierung tut nichts** — der getippte Text steht schon im
   * Feld, und ein versehentlich übernommener Vorschlag überschriebe Preis und
   * Einheit einer Position, die der Nutzer gerade selbst erfasst.
   */
  const taste = (event: React.KeyboardEvent) => {
    if (!aktiv || treffer.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setMarkiert((m) => (m + 1) % treffer.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setMarkiert((m) => (m <= 0 ? treffer.length - 1 : m - 1));
    } else if (event.key === "Enter" && markiert >= 0) {
      event.preventDefault();
      uebernehmen(markiert);
    } else if (event.key === "Escape") {
      setOffen(false);
    }
  };

  return (
    <div className="invoice-titel-feld" ref={box}>
      <input
        value={wert}
        autoFocus={autoFocus}
        onChange={(event) => { onWert(event.target.value); setOffen(true); }}
        onFocus={() => setOffen(true)}
        onKeyDown={taste}
        placeholder={art === "block"
          ? "Überschrift — tippen schlägt Bausteine vor"
          : "Bezeichnung — tippen durchsucht den Positionskatalog"}
        autoComplete="off"
        role="combobox"
        aria-expanded={aktiv && treffer.length > 0}
        aria-autocomplete="list"
      />
      {aktiv && treffer.length > 0 && (
        <div className="invoice-titel-treffer" role="listbox">
          {treffer.map((t, index) => {
            const schluessel = t.art === "block" ? t.block.title : t.art === "pflanze" ? t.pflanze.title : t.position.title;
            const wirdGeloescht = loeschenSchluessel === schluessel;
            return (
              <div className="invoice-titel-treffer-zeile" key={`${t.art}-${schluessel}-${index}`}>
                <div className={"invoice-titel-treffer-row" + (index === markiert ? " is-markiert" : "")}>
                  <button
                    type="button"
                    className="invoice-titel-treffer-uebernehmen"
                    onMouseEnter={() => setMarkiert(index)}
                    onClick={() => uebernehmen(index)}
                  >
                    {t.art === "block" ? (
                      <>
                        <span>
                          <strong><i className="invoice-catalog-tag">Block</i>{t.block.title}</strong>
                          <small>
                            {t.block.lines.length === 1
                              ? "1 Position" : `${t.block.lines.length} Positionen`}
                          </small>
                        </span>
                        <span className="invoice-catalog-price">
                          {euro(t.block.lines.reduce((a, l) => a + (Number(l.unit_price_net) || 0), 0))}
                          <small>Summe je 1×</small>
                        </span>
                      </>
                    ) : t.art === "pflanze" ? (
                      <>
                        <span>
                          <strong><i className="invoice-catalog-tag is-pflanze">Pflanze</i>{t.pflanze.title}</strong>
                          <small>{t.pflanze.description || t.pflanze.typ}</small>
                          <small className="invoice-pflanze-ek">
                            {t.pflanze.einkauf_netto != null
                              ? `EK ${euro(t.pflanze.einkauf_netto)} × ${String(t.pflanze.aufschlag).replace(".", ",")}${t.pflanze.lieferant ? ` · ${t.pflanze.lieferant}` : ""}`
                              : "Noch kein Lieferantenpreis — Preis im Angebot eintragen"}
                            {!t.pflanze.geprueft && " · ungeprüft"}
                          </small>
                        </span>
                        <span className="invoice-catalog-price">
                          {t.pflanze.einkauf_netto != null ? euro(Number(t.pflanze.unit_price_net) || 0) : "—"}
                          <small>je Stück</small>
                        </span>
                      </>
                    ) : (
                      <>
                        <span>
                          <strong>{t.position.title}</strong>
                          <small>{t.position.description || "Ohne Beschreibung"}</small>
                        </span>
                        <span className="invoice-catalog-price">
                          {euro(Number(t.position.unit_price_net) || 0)}
                          <small>je {t.position.unit_label || "Stück"}</small>
                        </span>
                      </>
                    )}
                  </button>
                  {/* Zurückhaltend, weil der Normalfall Übernehmen ist, nicht Pflegen —
                      ein versehentliches Löschen aus einem Autocomplete wäre besonders
                      ärgerlich (Benjamin, 01.09.2026). */}
                  {t.art !== "pflanze" && <span className="invoice-titel-treffer-tools">
                    {t.art === "position" && (
                      <button
                        type="button"
                        title="In der Bibliothek bearbeiten"
                        onClick={() => bearbeitenOeffnen(t.position)}
                      >
                        ✎
                      </button>
                    )}
                    <button
                      type="button"
                      title={`Aus der Bibliothek löschen`}
                      onClick={() => { setBearbeitetSchluessel(null); setLoeschenSchluessel(schluessel); }}
                    >
                      🗑
                    </button>
                  </span>}
                </div>

                {wirdGeloescht && (
                  <div className="invoice-titel-loeschen">
                    <span>
                      <strong>„{schluessel}" aus der Bibliothek löschen?</strong> Aus dem Beleg wird nichts entfernt.
                    </span>
                    <span className="invoice-titel-loeschen-aktionen">
                      <button type="button" className="invoice-button" onClick={() => setLoeschenSchluessel(null)}>
                        Behalten
                      </button>
                      <button
                        type="button"
                        className="invoice-button danger"
                        onClick={() => t.art !== "pflanze" && loeschenBestaetigen(t.art, schluessel)}
                      >
                        Löschen
                      </button>
                    </span>
                  </div>
                )}
              </div>
            );
          })}
          <p className="invoice-titel-hinweis">
            ↑↓ wählen · Enter übernimmt {art === "block" ? "den Baustein" : "die Position"} ·
            nichts Passendes? Einfach weitertippen — der Text bleibt, wie er ist.
          </p>
        </div>
      )}

      {/* Eigenständiger Dialog statt Anhängsel an der Trefferliste: die
          Karte darüber hat `overflow: hidden` (Redesign 01.09.2026) und
          schnitt das Pflegeformular ab, sobald wenig Platz nach unten blieb.
          Ein zentrierter Dialog ist außerdem die angenehm zu bearbeitende
          Größe, die Benjamin für diesen Zustand wollte — kein Autocomplete
          mehr, sondern ein richtiges Formular (Benjamin, 01.09.2026). */}
      {bearbeitetSchluessel && entwurf && (
        <div className="invoice-pflege-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setBearbeitetSchluessel(null); }}>
          <div className="invoice-pflege-dialog" role="dialog" aria-modal="true" aria-label="In der Bibliothek bearbeiten">
            <div className="invoice-titel-pflege-kopf">
              <span>In der Bibliothek bearbeiten</span>
              <small>Der Beleg bleibt unangetastet</small>
            </div>
            <div className="invoice-titel-pflege-felder">
              <label>
                <span>Titel</span>
                <input
                  value={entwurf.title}
                  onChange={(e) => setEntwurf({ ...entwurf, title: e.target.value })}
                />
              </label>
              <label>
                <span>Einheit</span>
                <input
                  value={entwurf.unit_label}
                  onChange={(e) => setEntwurf({ ...entwurf, unit_label: e.target.value })}
                />
              </label>
              <label>
                <span>Preis netto</span>
                <input
                  type="number" step="0.01"
                  value={entwurf.unit_price_net}
                  onChange={(e) => setEntwurf({ ...entwurf, unit_price_net: e.target.value })}
                />
              </label>
              <label>
                <span>USt.</span>
                <select
                  value={entwurf.vat_rate_percent}
                  onChange={(e) => setEntwurf({ ...entwurf, vat_rate_percent: Number(e.target.value) })}
                >
                  <option value={19}>19 %</option><option value={7}>7 %</option><option value={0}>0 %</option>
                </select>
              </label>
              <label className="invoice-titel-pflege-beschreibung">
                <span>Beschreibung</span>
                <textarea
                  value={entwurf.description}
                  onChange={(e) => setEntwurf({ ...entwurf, description: e.target.value })}
                />
              </label>
            </div>
            {pflegeHinweis && <p className="invoice-titel-pflege-fehler">{pflegeHinweis}</p>}
            <div className="invoice-titel-pflege-aktionen">
              <button type="button" className="invoice-button" onClick={() => setBearbeitetSchluessel(null)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="invoice-button primary"
                disabled={speichern.isPending}
                onClick={bearbeitenSpeichern}
              >
                In der Bibliothek speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
